/* @flow */
/**
 * Table type: tables are collections of data. Most often used for mapping purposes.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 * @typedef { Object } Table
 * @memberof TableService
 */
import papa from 'papaparse'
import _ from 'lodash'
import Typenames from './Typenames'
import { User } from 'services'
import { ActionTypes, store } from 'store'

export type Table = {
  name: string,
  version?: string,
  csv: string
}

const TYPENAME = Typenames.TABLE

/**
 * Creates a new table.
 * @function
 * @memberof TableService
 *
 * @return { Promise<string> } The id of the added table.
 */
const createTable = (table: Table): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, { typename: TYPENAME, item: table }).then(r => {
    User.addEdit({ type: TYPENAME, id: r.id, name: table.name, routeParams: { tableId: r.id } })
    return r
  })
}

/**
 * Reads a Table, flattening 'descriptive'.
 * @function
 * @memberof TableService
 *
 * @return { Promise<Table> } The table.
 */
const readTable = (id: string): Promise<Table> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id })
}

/**
 * Updates a table.
 * @function
 * @memberof TableService
 *
 * @return { Promise<any> }
 */
const updateTable = (id: string, table: Table, newCsv: string): Promise<PostResponse> => {
  return store
    .dispatch(ActionTypes.UPDATE, {
      typename: TYPENAME,
      id: id,
      item: { ...table, csv: newCsv }
    })
    .then(r => {
      User.addEdit({ type: TYPENAME, id, name: table.name, routeParams: { tableId: id } })
      return r
    })
}

/**
 * Deletes a table.
 * @function
 * @memberof TableService
 *
 * @return { Promise<any> }
 */
const deleteTable = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all tables, flattening descriptive.
 * @function
 * @memberof TableService
 *
 * @return { Promise<Array<Table>> } All the tables.
 */
const readAllTables = (): Promise<Array<Table>> => {
  return store.dispatch(ActionTypes.GET, TYPENAME)
}

/**
 * Parses csv to table data - header and rows.
 *
 * @function
 * @memberof FieldService
 */
const parseCsvToTableData = (csv: string) => {
  const parsedCsv = papa.parse(csv, { header: false })
  let header: Array<{ v: string }> = _.map(_.head(parsedCsv.data), f => {
    return { v: f }
  })
  let rows: Array<Array<{ v: string }>> = _.map(_.tail(parsedCsv.data), row =>
    _.map(row, d => ({ v: d }))
  )
  return { header, rows }
}

/**
 * Gets the difference in header and rows between baseHeader and changed header, and baseRows and changed rows.
 *
 * @function
 * @memberof FieldService
 */
const getTableDiff = (baseTableData: Object, changedTableData: Object) => {
  let rowsDiff = getRowsDiff(baseTableData.rows, changedTableData.rows)
  let headerDiff = getHeaderDiff(baseTableData.header, changedTableData.header)
  return { rowsDiff, headerDiff }
}

/**
 * Gets the difference between base rows and changed rows.
 *
 * @function
 * @memberof FieldService
 */
const getRowsDiff = (base: Array<any>, changed: Array<any>) => {
  const maxRows = Math.max(base ? base.length : 0, changed ? changed.length : 0)
  const maxColumns = Math.max(
    base && base[0] ? base[0].length : 0,
    changed && changed[0] ? changed[0].length : 0
  )

  let diff = []
  for (let i = 0; i < maxRows; i++) {
    diff.push([])
  }

  for (let i = 0; i < maxRows; i++) {
    for (let j = 0; j < maxColumns; j++) {
      let baseCell = base && base[i] && base[i][j] ? base[i][j].v : null
      let changedCell = changed && changed[i] && changed[i][j] ? changed[i][j].v : null
      diff[i][j] = getCellDiff(baseCell, changedCell)
    }
  }
  return diff
}

/**
 * Gets the difference between base header and changed header.
 *
 * @function
 * @memberof FieldService
 */
const getHeaderDiff = (base: Array<any>, changed: Array<any>) => {
  let maxCells = Math.max(base.length, changed.length)

  let diff = []
  for (let i = 0; i < maxCells; i++) {
    let baseCell = base[i] ? base[i].v : null
    let changedCell = changed[i] ? changed[i].v : null
    diff[i] = getCellDiff(baseCell, changedCell)
  }

  return diff
}

/**
 * Gets the cell diff providing information if the cell was changed, removed, added, or nothing was changed.
 *
 * @function
 * @memberof FieldService
 */
const getCellDiff = (baseCell: any, changedCell: any) => {
  if (!_.isNull(baseCell) && _.isNull(changedCell)) {
    return { v: baseCell, action: 'removed' }
  } else if (_.isNull(baseCell) && !_.isNull(changedCell)) {
    return { v: changedCell, action: 'added' }
  } else if (baseCell !== changedCell) {
    return { v: changedCell, action: 'changed' }
  } else {
    return { v: baseCell, action: '' }
  }
}

const self = {
  createTable,
  readTable,
  updateTable,
  deleteTable,
  readAllTables,
  parseCsvToTableData,
  getTableDiff
}

/**
 * Table service: CRUD interface for tables.
 * @module TableService
 * @namespace TableService
 * @version 1.0.0
 */
export default self
